import { createContext, Dispatch, ReactNode, SetStateAction, useMemo } from 'react';
import { FormikErrors } from 'formik';

import { ModalField, SessionStorageKeys, WidgetBehavior } from '../../types';
import { WidgetId } from '../../constants';

export type EmbeddedWidgetContextType<T> = {
  behavior?: WidgetBehavior;
  currentWidgetErrors: FormikErrors<T> | null;
  getCustomParamValues?: () => Record<string, any>;
  modalFields: ModalField[];
  sessionStorageKeys?: SessionStorageKeys | undefined;
  setCurrentWidgetErrors: Dispatch<SetStateAction<FormikErrors<T>>>;
  showFieldsRequiredLabel?: boolean;
  widgetId: WidgetId;
};

const EmbeddedWidgetContext = createContext<EmbeddedWidgetContextType<any> | undefined>(undefined);

const EmbeddedWidgetProvider = <T,>({
  behavior,
  children,
  currentWidgetErrors,
  getCustomParamValues,
  sessionStorageKeys,
  setCurrentWidgetErrors,
  modalFields,
  widgetId,
  showFieldsRequiredLabel,
}: {
  behavior?: WidgetBehavior;
  children: ReactNode;
  currentWidgetErrors: FormikErrors<T> | null;
  getCustomParamValues?: () => Record<string, any>;
  modalFields: ModalField[];
  sessionStorageKeys?: SessionStorageKeys;
  setCurrentWidgetErrors: Dispatch<SetStateAction<FormikErrors<T>>>;
  widgetId: WidgetId;
  showFieldsRequiredLabel?: boolean;
}) => {
  const value = useMemo(
    () => ({
      behavior,
      currentWidgetErrors,
      getCustomParamValues,
      modalFields,
      sessionStorageKeys,
      setCurrentWidgetErrors,
      showFieldsRequiredLabel,
      widgetId,
    }),
    [
      behavior,
      currentWidgetErrors,
      getCustomParamValues,
      modalFields,
      sessionStorageKeys,
      setCurrentWidgetErrors,
      showFieldsRequiredLabel,
      widgetId,
    ],
  );

  return <EmbeddedWidgetContext.Provider value={value}>{children}</EmbeddedWidgetContext.Provider>;
};

export { EmbeddedWidgetContext, EmbeddedWidgetProvider };
